import * as React from "react";
import { Link } from "react-router-dom";
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@eduviaos/ui-kit";

/**
 * Phase B2.4 — application-specific breadcrumb compositions.
 *
 * Each helper renders the static prefix of the breadcrumb trail (e.g.
 * "Platform · Tenants") and accepts `children` for the trailing
 * page-specific segment that the page itself owns. This keeps the
 * "I am here" segment expressive (tenant name, form name, etc.)
 * without forcing a global breadcrumb registry.
 *
 * The leftmost segment is always "Platform" — a non-link anchor that
 * orients SAs operating across multiple installations. It also sets a
 * predictable hit area on mobile where the back gesture can be slow.
 */

/**
 * Crumb composition: `Platform · Tenants · <children>` where the
 * caller is responsible for rendering the final crumb (link OR page).
 * On the `/tenants` index, the caller passes `<BreadcrumbPage>Tenants</BreadcrumbPage>`
 * — so the Tenants segment behaves as a link only on detail pages.
 */
export function TenantsBreadcrumbs({ children }: { children: React.ReactNode }) {
  // Walk children: if the LAST segment is the literal "Tenants", we
  // skip rendering our own "Tenants" link to avoid duplication. We
  // detect this by looking for `BreadcrumbPage` content "Tenants".
  // Simpler: always render the prefix, callers control the final crumb.
  return (
    <Breadcrumb>
      <BreadcrumbList>
        <BreadcrumbItem className="hidden sm:inline-flex">
          <BreadcrumbLink className="text-muted-foreground/80">Platform</BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbSeparator className="hidden sm:inline-flex" />
        {children}
      </BreadcrumbList>
    </Breadcrumb>
  );
}

/**
 * `Platform · Tenants · <tenantName>` — for the tenant detail page
 * and any nested routes (forms editor, branding, etc.).
 */
export function TenantDetailBreadcrumbs({
  tenantName,
  trail,
}: {
  /** Display name of the current tenant. */
  tenantName: string;
  /** Optional further trail segments past the tenant name. The LAST
   *  entry is rendered as the current page (non-link). */
  trail?: Array<{ label: string; to?: string }>;
}) {
  const crumbs = trail ?? [];
  return (
    <Breadcrumb>
      <BreadcrumbList>
        <BreadcrumbItem className="hidden sm:inline-flex">
          <BreadcrumbLink className="text-muted-foreground/80">Platform</BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbSeparator className="hidden sm:inline-flex" />
        <BreadcrumbItem>
          <BreadcrumbLink asChild>
            <Link to="/tenants">Tenants</Link>
          </BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbSeparator />
        {crumbs.length === 0 ? (
          <BreadcrumbItem>
            <BreadcrumbPage>{tenantName}</BreadcrumbPage>
          </BreadcrumbItem>
        ) : (
          <>
            <BreadcrumbItem>
              <BreadcrumbLink asChild>
                <Link to={`/tenants/${encodeURIComponent(tenantName)}`}>{tenantName}</Link>
              </BreadcrumbLink>
            </BreadcrumbItem>
            {crumbs.map((c, i) => {
              const isLast = i === crumbs.length - 1;
              return (
                <React.Fragment key={`${c.label}-${i}`}>
                  <BreadcrumbSeparator />
                  <BreadcrumbItem>
                    {isLast || !c.to ? (
                      <BreadcrumbPage>{c.label}</BreadcrumbPage>
                    ) : (
                      <BreadcrumbLink asChild>
                        <Link to={c.to}>{c.label}</Link>
                      </BreadcrumbLink>
                    )}
                  </BreadcrumbItem>
                </React.Fragment>
              );
            })}
          </>
        )}
      </BreadcrumbList>
    </Breadcrumb>
  );
}

/**
 * Generic `Platform · Settings · <children>` for global SA settings.
 */
export function SettingsBreadcrumbs({ children }: { children: React.ReactNode }) {
  return (
    <Breadcrumb>
      <BreadcrumbList>
        <BreadcrumbItem className="hidden sm:inline-flex">
          <BreadcrumbLink className="text-muted-foreground/80">Platform</BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbSeparator className="hidden sm:inline-flex" />
        <BreadcrumbItem>
          <BreadcrumbLink asChild>
            <Link to="/dashboard">Settings</Link>
          </BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbSeparator />
        {children}
      </BreadcrumbList>
    </Breadcrumb>
  );
}

/**
 * `Platform · <pageName>` — used by simple, top-level pages
 * (Dashboard, Verticals, Capabilities, Audit log, Billing, …).
 */
export function PlatformBreadcrumbs({ children }: { children: React.ReactNode }) {
  return (
    <Breadcrumb>
      <BreadcrumbList>
        <BreadcrumbItem className="hidden sm:inline-flex">
          <BreadcrumbLink className="text-muted-foreground/80">Platform</BreadcrumbLink>
        </BreadcrumbItem>
        <BreadcrumbSeparator className="hidden sm:inline-flex" />
        {children}
      </BreadcrumbList>
    </Breadcrumb>
  );
}
